import { api } from '@creditsights/api-ui-web';
import { intersection } from 'lodash';
import { DealMarketStatus, ModerationStatus, SecurityType } from '../models/CompanyDeal';
import { SecuritiesResults } from '../models/Securities';
import { filterMapper, getOptionsWithTwoLevels } from '../utils/filter.utils';
import { fixCountryNamesBaseOnTags } from '../utils/tag.utils';
import { fetchFilters } from './filter.service';

const marketStatusFilterMapping = {
  [SecurityType.BOND]: {
    [DealMarketStatus.InMarket]: { values: ['Announced'] },
    [DealMarketStatus.Expected]: { values: ['Expected', 'Rumoured'] },
    [DealMarketStatus.Hung]: { values: ['Cancelled', 'Pulled'] },
    [DealMarketStatus.Completed]: {
      excluded_values: ['Announced', 'Cancelled', 'Pulled', 'Expected', 'Rumoured'],
    },
  },
  [SecurityType.LOAN]: {
    [DealMarketStatus.InMarket]: { values: ['Announced'] },
    [DealMarketStatus.Expected]: { values: ['Expected', 'Rumoured'] },
    [DealMarketStatus.Hung]: { values: ['Cancelled', 'Hung Syndication (Outstanding)', 'Hung Syndication'] },
    [DealMarketStatus.Completed]: {
      excluded_values: ['Announced', 'Cancelled', 'Hung Syndication (Outstanding)', 'Hung Syndication', 'Expected', 'Rumoured'],
    },
  },
  [SecurityType.COMBINED]: {
    [DealMarketStatus.InMarket]: { values: ['Announced'] },
    [DealMarketStatus.Expected]: { values: ['Expected', 'Rumoured'] },
    [DealMarketStatus.Hung]: { values: ['Cancelled', 'Hung Syndication (Outstanding)', 'Hung Syndication', 'Pulled'] },
    [DealMarketStatus.Completed]: {
      excluded_values: ['Announced', 'Cancelled', 'Pulled', 'Hung Syndication (Outstanding)', 'Hung Syndication', 'Expected', 'Rumoured'],
    },
  },
};

const getDealStatusFilter = (type: SecurityType, marketStatus: DealMarketStatus, dealsStatusFilter: { values: string[] }) => {
  const baseFilter = marketStatusFilterMapping[type][marketStatus];

  if (!baseFilter) {
    return dealsStatusFilter;
  }
  if (!dealsStatusFilter) {
    return baseFilter;
  }

  return {
    ...baseFilter,
    values: baseFilter.values ? intersection(baseFilter.values, dealsStatusFilter.values) : dealsStatusFilter.values,
  };
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const fetchSecurities = async (type, sort, marketStatus, filterConfig, pageSize, page = 1) => {
  const { issuer_sectors: sectors, deal_status: dealStatus, ...otherFilters } = filterConfig;
  const impliedFilter = [...(sectors?.values || [])];

  const filters = {
    issuer_implies: impliedFilter.length > 0 ? { values: impliedFilter } : undefined,
    ...otherFilters,
  };

  const { data } = await api.post<SecuritiesResults>('common/v1/securities', {
    filters: {
      archive: { values: [false] },
      security_type: { values: type === 'combined' ? ['loan', 'bond'] : [type] },
      deal_status: getDealStatusFilter(type, marketStatus, dealStatus),
      moderation: { values: [ModerationStatus.PUBLISHED, ModerationStatus.SENT] },
      ...filters,
    },
    skip: (page - 1) * pageSize,
    take: pageSize,
    sort: sort.field,
    sortDesc: !sort.asc,
  });

  return data;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const fetchSecurityFilters = async (type) => {
  const filters = [
    {
      id: 'company_name',
      name: 'Company',
    },
    {
      id: 'issuer_regions',
      name: 'Region',
    },
    {
      id: 'country_tag',
      name: 'Country or Territory',
    },
    {
      id: 'issuer_sectors',
      name: 'Sector/Subsector',
    },
    // Hidden in CFE-12
    // {
    //   id: 'issuer_credit_grades',
    //   name: 'Credit Grade',
    // },
    {
      id: 'uop',
      name: 'UOP',
    },
    // Temporarily hidden in FCI-1795
    // {
    //   id: 'deal_type',
    //   name: 'Type',
    // },
    {
      id: 'issuers',
      name: 'Issuer',
    },
    {
      id: 'leads.company',
      name: 'Lead',
    },
    {
      id: 'sponsors.sponsor',
      name: 'Sponsor',
    },
    {
      id: 'deal_status',
      name: 'Deal status',
    },
  ];

  const promises = filters.map((filter) =>
    api.post(`common/v1/securities/filters/${filter.id}`, {
      filters: {
        archive: { values: [false] },
        moderation: { values: [ModerationStatus.PUBLISHED, ModerationStatus.SENT] },
        security_type: { values: type === 'combined' ? ['loan', 'bond'] : [type] },
      },
      take: 5000,
    })
  );

  const [results, tagFilters] = await Promise.all([Promise.all(promises), fetchFilters()]);
  const resp: any = {};

  results.forEach((result, index) => {
    const filter = filters[index];

    resp[filter.id] = {
      name: filter.name,
      data: filterMapper(result.data.filter((i) => !!i)),
    };
  });

  resp.issuer_regions.data = tagFilters.config.region;
  resp.issuer_sectors.data = getOptionsWithTwoLevels(tagFilters.config.sector);
  resp.country_tag.data = fixCountryNamesBaseOnTags(resp.country_tag.data, tagFilters.map);
  return resp;
};
